import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Eager: cold-entry pages, always-rendered layout/guard shells, and the
// Suspense fallback itself. Everything else is lazy-loaded (see below).
import { HomePage } from "@/pages/HomePage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { LoginPage } from "@/pages/auth/LoginPage";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { AccountLayout } from "@/components/layouts/AccountLayout";
import { BookingLayout } from "@/components/layouts/BookingLayout";
import { WidgetLayout } from "@/components/layouts/WidgetLayout";
import { AuthProvider } from "@/components/auth/AuthProvider";
import { PostHogIdentity } from "@/components/analytics/PostHogIdentity";
import { useRealtimeInvalidation } from "@/hooks/useRealtimeInvalidation";

/**
 * Headless bridge: subscribes to the user's realtime channel + invalidates caches.
 * @returns Nothing (renders null).
 */
function RealtimeBridge() {
  useRealtimeInvalidation();
  return null;
}
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { GuestRoute } from "@/components/auth/GuestRoute";
import { BusinessRoute } from "@/components/auth/BusinessRoute";
import { RequireApprovedDriver } from "@/components/auth/RequireApprovedDriver";
import { Spinner } from "@/components/ui/Spinner";
import { Toaster } from "@/components/ui/Toaster";

// Lazy route leaves — code-split out of the initial bundle. Pages use named
// exports, so map each to a default for React.lazy.
const RegisterPage = lazy(() => import("@/pages/auth/RegisterPage").then((m) => ({ default: m.RegisterPage })));
const BusinessRegistrationPage = lazy(() => import("@/pages/auth/BusinessRegistrationPage").then((m) => ({ default: m.BusinessRegistrationPage })));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })));
const AcceptInvitationPage = lazy(() => import("@/pages/auth/AcceptInvitationPage").then((m) => ({ default: m.AcceptInvitationPage })));
const VerifyAccountPage = lazy(() => import("@/pages/auth/VerifyAccountPage").then((m) => ({ default: m.VerifyAccountPage })));
const AuthCallbackPage = lazy(() => import("@/pages/auth/AuthCallbackPage").then((m) => ({ default: m.AuthCallbackPage })));
const DashboardHomePage = lazy(() => import("@/pages/dashboard/DashboardHomePage").then((m) => ({ default: m.DashboardHomePage })));
const DashboardRedirectPage = lazy(() => import("@/pages/dashboard/DashboardRedirectPage").then((m) => ({ default: m.DashboardRedirectPage })));
const ReservationsListPage = lazy(() => import("@/pages/dashboard/reservations/ReservationsListPage").then((m) => ({ default: m.ReservationsListPage })));
const ReservationDetailPage = lazy(() => import("@/pages/dashboard/reservations/ReservationDetailPage").then((m) => ({ default: m.ReservationDetailPage })));
const NewReservationPage = lazy(() => import("@/pages/dashboard/reservations/NewReservationPage").then((m) => ({ default: m.NewReservationPage })));
const TeamPage = lazy(() => import("@/pages/dashboard/TeamPage").then((m) => ({ default: m.TeamPage })));
const DispatchPage = lazy(() => import("@/pages/dashboard/DispatchPage").then((m) => ({ default: m.DispatchPage })));
const DashboardMessagesPage = lazy(() => import("@/pages/dashboard/MessagesPage").then((m) => ({ default: m.DashboardMessagesPage })));
const VehiclesPage = lazy(() => import("@/pages/dashboard/VehiclesPage").then((m) => ({ default: m.VehiclesPage })));
const SettingsPage = lazy(() => import("@/pages/dashboard/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const AnalyticsPage = lazy(() => import("@/pages/dashboard/AnalyticsPage").then((m) => ({ default: m.AnalyticsPage })));
const PricingZonesPage = lazy(() => import("@/pages/dashboard/PricingZonesPage").then((m) => ({ default: m.PricingZonesPage })));
const AccountHomePage = lazy(() => import("@/pages/account/AccountHomePage").then((m) => ({ default: m.AccountHomePage })));
const NotificationsPage = lazy(() => import("@/pages/account/NotificationsPage").then((m) => ({ default: m.NotificationsPage })));
const MessagesPage = lazy(() => import("@/pages/account/MessagesPage").then((m) => ({ default: m.MessagesPage })));
const ProfilePage = lazy(() => import("@/pages/account/ProfilePage").then((m) => ({ default: m.ProfilePage })));
const BecomeDriverPage = lazy(() => import("@/pages/account/BecomeDriverPage").then((m) => ({ default: m.BecomeDriverPage })));
const MyRidesPage = lazy(() => import("@/pages/account/MyRidesPage").then((m) => ({ default: m.MyRidesPage })));
const MyBookingsPage = lazy(() => import("@/pages/account/MyBookingsPage").then((m) => ({ default: m.MyBookingsPage })));
const AvailabilityPage = lazy(() => import("@/pages/account/AvailabilityPage").then((m) => ({ default: m.AvailabilityPage })));
const DriverMembershipsPage = lazy(() => import("@/pages/account/driver/DriverMembershipsPage").then((m) => ({ default: m.DriverMembershipsPage })));
const DriverDocumentsPage = lazy(() => import("@/pages/account/driver/DriverDocumentsPage").then((m) => ({ default: m.DriverDocumentsPage })));
const DriverVehiclesPage = lazy(() => import("@/pages/account/driver/DriverVehiclesPage").then((m) => ({ default: m.DriverVehiclesPage })));
const DriverAnalyticsPage = lazy(() => import("@/pages/account/driver/DriverAnalyticsPage").then((m) => ({ default: m.DriverAnalyticsPage })));
const PendingApprovalPage = lazy(() => import("@/pages/account/PendingApprovalPage").then((m) => ({ default: m.PendingApprovalPage })));
const ApplicationRejectedPage = lazy(() => import("@/pages/account/ApplicationRejectedPage").then((m) => ({ default: m.ApplicationRejectedPage })));
const DriverReapplyPage = lazy(() => import("@/pages/account/DriverReapplyPage").then((m) => ({ default: m.DriverReapplyPage })));
const CompanyReapplyPage = lazy(() => import("@/pages/account/CompanyReapplyPage").then((m) => ({ default: m.CompanyReapplyPage })));
const SelectPlanPage = lazy(() => import("@/pages/account/SelectPlanPage").then((m) => ({ default: m.SelectPlanPage })));
const UpgradeCheckoutPage = lazy(() => import("@/pages/account/UpgradeCheckoutPage").then((m) => ({ default: m.UpgradeCheckoutPage })));
const UpgradeSuccessPage = lazy(() => import("@/pages/account/UpgradeSuccessPage").then((m) => ({ default: m.UpgradeSuccessPage })));
const StartBusinessPage = lazy(() => import("@/pages/account/StartBusinessPage").then((m) => ({ default: m.StartBusinessPage })));
const ShareBookingPage = lazy(() => import("@/pages/account/ShareBookingPage").then((m) => ({ default: m.ShareBookingPage })));
const BookingPage = lazy(() => import("@/pages/booking/BookingPage").then((m) => ({ default: m.BookingPage })));
const BookingConfirmationPage = lazy(() => import("@/pages/booking/BookingConfirmationPage").then((m) => ({ default: m.BookingConfirmationPage })));
const ReservationViewPage = lazy(() => import("@/pages/reservation").then((m) => ({ default: m.ReservationViewPage })));
const ReservationEditPage = lazy(() => import("@/pages/reservation").then((m) => ({ default: m.ReservationEditPage })));
const ReservationCancelPage = lazy(() => import("@/pages/reservation").then((m) => ({ default: m.ReservationCancelPage })));
const TermsPage = lazy(() => import("@/pages/legal/TermsPage").then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import("@/pages/legal/PrivacyPage").then((m) => ({ default: m.PrivacyPage })));
const DriverTermsPage = lazy(() => import("@/pages/legal/DriverTermsPage").then((m) => ({ default: m.DriverTermsPage })));
const SmsTermsPage = lazy(() => import("@/pages/legal/SmsTermsPage").then((m) => ({ default: m.SmsTermsPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

/**
 * Main application component.
 * @returns The application with routing
 */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <RealtimeBridge />
          <Toaster />
          <PostHogIdentity>
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center">
                  <Spinner size="lg" />
                </div>
              }
            >
            <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />

          {/* Public legal pages (linked from consent checkboxes + footer) */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/driver-terms" element={<DriverTermsPage />} />
          <Route path="/sms" element={<SmsTermsPage />} />

          {/* The standalone marketing pages were retired in favor of homepage
              sections. These paths now redirect to the matching homepage anchor
              so old links + the header/footer nav resolve; the home route's
              hash-scroll effect jumps to the section. "Contact" → the footer. */}
          <Route path="/features" element={<Navigate to="/#features" replace />} />
          <Route path="/about" element={<Navigate to="/#about" replace />} />
          <Route path="/contact" element={<Navigate to="/#contact" replace />} />

          {/* Public booking routes - company-first pattern */}
          <Route path="/:companySlug/book" element={<BookingLayout />}>
            <Route index element={<BookingPage />} />
            <Route path="confirmation" element={<BookingConfirmationPage />} />
          </Route>

          {/* Embeddable booking widget (Business Plus). Chromeless variant
              of the booking flow that operators iframe into their own
              websites. See WidgetLayout for postMessage auto-resize and
              docs/api-specification.md for the gating + CSP posture. */}
          <Route path="/widget/:companySlug" element={<WidgetLayout />}>
            <Route index element={<BookingPage />} />
            <Route path="confirmation" element={<BookingConfirmationPage />} />
          </Route>

          {/* Public reservation view/edit/cancel routes (token-based) */}
          <Route path="/reservation/:token" element={<ReservationViewPage />} />
          <Route path="/reservation/:token/edit" element={<ReservationEditPage />} />
          <Route path="/reservation/:token/cancel" element={<ReservationCancelPage />} />

          {/* Auth routes - wrapped with GuestRoute to redirect authenticated users */}
          <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
          <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
          <Route path="/register/business" element={<GuestRoute><BusinessRegistrationPage /></GuestRoute>} />
          <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify" element={<VerifyAccountPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/invite/:token" element={<AcceptInvitationPage />} />

          {/* Dashboard redirect (protected) */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardRedirectPage />
              </ProtectedRoute>
            }
          />

          {/* Personal account routes (protected) */}
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <AccountLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AccountHomePage />} />
            <Route path="bookings" element={<MyBookingsPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="settings" element={<ProfilePage />} />
            <Route path="become-driver" element={<BecomeDriverPage />} />
            {/* Driver approval status routes */}
            <Route path="pending-approval" element={<PendingApprovalPage />} />
            <Route path="application-rejected" element={<ApplicationRejectedPage />} />
            <Route path="driver-reapply" element={<DriverReapplyPage />} />
            <Route path="business-reapply" element={<CompanyReapplyPage />} />
            {/* Upgrade flow */}
            <Route path="select-plan" element={<SelectPlanPage />} />
            <Route path="upgrade/checkout" element={<UpgradeCheckoutPage />} />
            <Route path="upgrade/success" element={<UpgradeSuccessPage />} />
            {/* Business creation for paid drivers */}
            <Route path="start-business" element={<StartBusinessPage />} />
            {/* Driver-specific routes. RequireApprovedDriver is a UX guard:
                pending drivers are sent to the status page instead of seeing
                silently-empty pages (the API enforces access on its own). */}
            <Route path="rides" element={<RequireApprovedDriver><MyRidesPage /></RequireApprovedDriver>} />
            <Route path="availability" element={<RequireApprovedDriver><AvailabilityPage /></RequireApprovedDriver>} />
            <Route path="share-booking" element={<RequireApprovedDriver><ShareBookingPage /></RequireApprovedDriver>} />
            <Route path="driver/memberships" element={<RequireApprovedDriver><DriverMembershipsPage /></RequireApprovedDriver>} />
            <Route path="driver/documents" element={<RequireApprovedDriver><DriverDocumentsPage /></RequireApprovedDriver>} />
            <Route path="driver/vehicles" element={<RequireApprovedDriver><DriverVehiclesPage /></RequireApprovedDriver>} />
            <Route path="driver/analytics" element={<RequireApprovedDriver><DriverAnalyticsPage /></RequireApprovedDriver>} />
          </Route>

          {/* Business dashboard routes - business-first pattern (protected) */}
          {/* Uses BusinessRoute to verify user has membership AND admin/manager role */}
          <Route
            path="/:companyId/dashboard"
            element={
              <BusinessRoute>
                <DashboardLayout />
              </BusinessRoute>
            }
          >
            <Route index element={<DashboardHomePage />} />
            <Route path="reservations" element={<ReservationsListPage />} />
            <Route path="reservations/:id" element={<ReservationDetailPage />} />
            <Route path="reservations/new" element={<NewReservationPage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="dispatch" element={<DispatchPage />} />
            <Route path="messages" element={<DashboardMessagesPage />} />
            <Route path="vehicles" element={<VehiclesPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="pricing" element={<PricingZonesPage />} />
            {/* Company Settings (incl. the Billing tab) is admin-only — the nav
                link is hidden from managers (useSidebarNavigation) and every
                write is admin-gated server-side, so the route itself must guard
                too (#208). Without this, a manager hitting the URL directly got
                a fully-editable admin form whose Save 403'd with a raw error. */}
            <Route
              path="settings"
              element={
                <BusinessRoute allowedRoles={["admin"]}>
                  <SettingsPage />
                </BusinessRoute>
              }
            />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
          </PostHogIdentity>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
